import { MAX_VISIBLE_AUTHORS } from "constants/config";
import { PaperDetails } from "helpers/api";
import { parseDateToUTC } from "helpers/date";
import useLabels from "hooks/useLabels";
import { useMemo, useState } from "react";

type PaperAuthorsAndPublishDateProps = {
  paper: PaperDetails;
};

function PaperAuthorsAndPublishDate({
  paper,
}: PaperAuthorsAndPublishDateProps) {
  const [pageLabels] = useLabels("screens.details");

  const [showMoreAuthors, setShowMoreAuthors] = useState(false);
  const moreAuthorsCount = useMemo(() => {
    return paper.authors.length - MAX_VISIBLE_AUTHORS;
  }, [paper.authors]);

  const publishDate = useMemo(() => {
    if (!paper.publish_date) return paper.year;

    const date = parseDateToUTC(paper.publish_date);
    if (!date) return paper.year;

    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    };

    return new Intl.DateTimeFormat("en-US", options).format(date);
  }, [paper]);

  return (
    <div className="flex flex-row items-center mt-2 gap-x-2">
      <div className="sm-normal text-fg-muted flex flex-wrap">
        {showMoreAuthors ? (
          <p data-testid="show-more-author">
            {pageLabels["published"]} {publishDate}&nbsp;&middot;&nbsp;
            {paper.authors.map((author: string, index: number) => {
              if (index === 0) return author;
              return `, ${author}`;
            })}
          </p>
        ) : (
          <p data-testid="hide-more-author">
            {pageLabels["published"]} {publishDate}&nbsp;&middot;&nbsp;
            {paper.authors
              .slice(0, MAX_VISIBLE_AUTHORS)
              .map((author: string, index: number) => {
                if (index === 0) return author;
                return `, ${author}`;
              })}
            {moreAuthorsCount > 0 && (
              <button
                data-testid="more-author-button"
                onClick={() => setShowMoreAuthors(true)}
                className="underline ml-2"
              >
                {pageLabels["more"].replace("{count}", moreAuthorsCount)}
              </button>
            )}
          </p>
        )}
      </div>
    </div>
  );
}

export default PaperAuthorsAndPublishDate;
